// Practical-update contracts (D-081, plan §§9.11 and 28.10).
//
// These are short operational notes attached to a support need, never a chat or a
// clinical record. The contract keeps note text out of URLs, audit payloads and
// notification payloads by construction: mutation inputs carry it only to the
// authorised function, while history and notification intent expose field names and
// opaque references only.

import { z } from "zod";

import type { CommonRejection, Result } from "../errors.js";
import { circleIdSchema, personIdSchema, supportNeedIdSchema } from "../primitives.js";

const practicalUpdateIdSchema = z.string().min(1, "An update identifier must not be empty.");
const practicalUpdateTextSchema = z
  .string()
  .trim()
  .min(1, "Add a short practical update.")
  .max(500, "Keep a practical update to 500 characters or fewer.");
const sourceChannelSchema = z.string().trim().min(1).max(60);

export const createSelfInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
  text: practicalUpdateTextSchema,
  includeInPrint: z.boolean().default(false),
  notificationRequested: z.boolean().default(false),
});

export const createProxyInput = createSelfInput.extend({
  responsiblePersonId: personIdSchema,
  sourceChannel: sourceChannelSchema,
});

export const updateInput = z
  .object({
    circleId: circleIdSchema,
    practicalUpdateId: practicalUpdateIdSchema,
    expectedRevision: z.number().int().nonnegative(),
    text: practicalUpdateTextSchema.optional(),
    includeInPrint: z.boolean().optional(),
    pinned: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine(
    (input) =>
      input.text !== undefined ||
      input.includeInPrint !== undefined ||
      input.pinned !== undefined ||
      input.sortOrder !== undefined,
    "Change at least one update field.",
  );

export const redactInput = z.object({
  circleId: circleIdSchema,
  practicalUpdateId: practicalUpdateIdSchema,
  expectedRevision: z.number().int().nonnegative(),
});

export type PracticalUpdateRejection = CommonRejection | { readonly kind: "already_redacted" };

export type PracticalUpdateEditMode = "self" | "proxy";

export interface PracticalUpdateView {
  readonly practicalUpdateId: string;
  readonly supportNeedId: string;
  readonly text: string | null;
  readonly responsiblePersonId: string;
  readonly responsibleName: string | null;
  readonly recordedByPersonId: string;
  readonly recordedByName: string | null;
  readonly source: "self" | "proxy";
  readonly sourceChannel: string | null;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly redactedAt: number | null;
  readonly revision: number;
  readonly pinned: boolean;
  readonly sortOrder: number;
  readonly includeInPrint: boolean;
  /** Epic 7 consumes this content-free preference; this feature does not deliver. */
  readonly notificationRequested: boolean;
  readonly editMode: PracticalUpdateEditMode | null;
  readonly redactMode: PracticalUpdateEditMode | null;
}

export interface PracticalUpdateHistoryEntry {
  readonly historyId: string;
  readonly action: "created" | "updated" | "redacted";
  readonly actorPersonId: string | null;
  readonly actorName: string | null;
  readonly occurredAt: number;
  /** Field names/codes only. Previous or current note text never enters audit history. */
  readonly changedFields: readonly string[];
  readonly sourceChannel: string | null;
}

/** Server-decided affordances accompany the need-scoped read (AUT-007). */
export interface PracticalUpdatesForNeedView {
  readonly canCreateSelf: boolean;
  readonly canViewHistory: boolean;
  readonly updates: readonly PracticalUpdateView[];
}

export interface PracticalUpdateWritten {
  readonly practicalUpdateId: string;
  readonly revision: number;
}

export type CreatePracticalUpdateResult = Result<PracticalUpdateWritten, PracticalUpdateRejection>;
export type UpdatePracticalUpdateResult = Result<PracticalUpdateWritten, PracticalUpdateRejection>;
export type RedactPracticalUpdateResult = Result<PracticalUpdateWritten, PracticalUpdateRejection>;

/** The only payload Epic 7 may turn into a notification job. No authored words. */
export interface PracticalUpdateNotificationIntent {
  readonly triggerType: "practical_update_added";
  readonly triggerId: string;
  readonly safePayload: {
    readonly supportNeedId: string;
    readonly reasonCode: "update_added";
  };
}
